import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CaretDown, CaretUp, CaretUpDown } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

// useTableSortState + SortableHeaderButton are the shared sort primitives,
// matching the shape of useTablePaginationState / TablePagination next door.
//
// Single-column, asc ↔ desc only:
//   - Clicking a column that isn't currently sorted sets it to ascending.
//   - Clicking the active column flips asc → desc → asc → … (no clear state).
//   - Initial state has no sort param in the URL — consumers fall back to
//     their own natural ordering (e.g. owner-first on Administrators,
//     last_name on Members) until the first click.
//
// State persists in the URL as `?sort=<col>&dir=asc|desc`, mirroring how
// pagination uses `?page=&size=`. Use `prefix` if a route hosts more than
// one sortable table at a time so the params don't collide.
//
// Sorting always resets pagination to page 1 — sort changes which rows
// land on which page, so staying on page 7 of the new ordering is rarely
// what the user wants.

export type SortDir = 'asc' | 'desc';

export interface TableSortState<T extends string> {
  // Active sort column, or null when no sort is active.
  sort: T | null;
  // Direction of the active sort. Meaningless when `sort` is null but the
  // caller can still read it without a null check (defaults to 'asc').
  dir: SortDir;
  // Click handler for a sortable header. Sets the column to asc on first
  // click; flips asc ↔ desc on subsequent clicks.
  toggle: (column: T) => void;
}

interface UseTableSortStateOpts<T extends string> {
  // Allowlist of column ids that may appear in the URL. Anything else is
  // ignored (treated as no sort) so a stale `?sort=removed_col` link doesn't
  // break the page or, worse, get sent unchecked to a server endpoint.
  columns: readonly T[];
  // Distinct query-param prefix when more than one sortable table lives on
  // the same route. Default: bare `sort` and `dir`.
  prefix?: string;
}

export function useTableSortState<T extends string>(
  opts: UseTableSortStateOpts<T>,
): TableSortState<T> {
  const [searchParams, setSearchParams] = useSearchParams();
  const sortKey = opts.prefix ? `${opts.prefix}_sort` : 'sort';
  const dirKey = opts.prefix ? `${opts.prefix}_dir` : 'dir';
  const pageKey = opts.prefix ? `${opts.prefix}_page` : 'page';

  const sort = useMemo<T | null>(() => {
    const raw = searchParams.get(sortKey);
    if (raw && (opts.columns as readonly string[]).includes(raw)) {
      return raw as T;
    }
    return null;
  }, [searchParams, sortKey, opts.columns]);

  const dir = useMemo<SortDir>(() => {
    const raw = searchParams.get(dirKey);
    return raw === 'desc' ? 'desc' : 'asc';
  }, [searchParams, dirKey]);

  function toggle(column: T) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const isActive = sort === column;
        const nextDir: SortDir = isActive
          ? dir === 'asc'
            ? 'desc'
            : 'asc'
          : 'asc';
        next.set(sortKey, column);
        if (nextDir === 'asc') next.delete(dirKey);
        else next.set(dirKey, nextDir);
        // Sort changes which rows land on which page — start over.
        next.delete(pageKey);
        return next;
      },
      { replace: true },
    );
  }

  return { sort, dir, toggle };
}

interface SortableHeaderButtonProps<T extends string> {
  column: T;
  label: string;
  state: TableSortState<T>;
  // Pass-through className lets consumers tweak the button's flex / padding
  // when slotted into a CSS-grid cell (Members, Teams) vs. a <th> primitive
  // (Administrators).
  className?: string;
}

// SortableHeaderButton renders the button + arrow indicator that goes inside
// any header cell. Wrap with <TableHead> for <Table>-primitive consumers, or
// drop into a CSS-grid header cell for the custom layouts. The button takes
// the full width of its container and aligns text left so it sits naturally
// where a normal header label used to live.
export function SortableHeaderButton<T extends string>({
  column,
  label,
  state,
  className,
}: SortableHeaderButtonProps<T>) {
  const { t } = useTranslation('common');
  const isActive = state.sort === column;
  const Icon = !isActive ? CaretUpDown : state.dir === 'asc' ? CaretUp : CaretDown;

  // Aria-label describes what activating the button will do, since the
  // visual arrow alone won't be picked up by every screen reader. The
  // resulting action depends on current state: a fresh column will sort
  // ascending; the active column flips direction.
  const ariaKey = !isActive
    ? 'tableSort.aria.activateAsc'
    : state.dir === 'asc'
      ? 'tableSort.aria.flipToDesc'
      : 'tableSort.aria.flipToAsc';

  return (
    <button
      type="button"
      onClick={() => state.toggle(column)}
      aria-label={t(ariaKey, { column: label })}
      aria-pressed={isActive}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill px-1 py-0.5 text-left transition-colors',
        'hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isActive ? 'text-foreground' : 'text-muted-foreground',
        className,
      )}
    >
      <span>{label}</span>
      <Icon
        className={cn(
          'h-3.5 w-3.5 shrink-0',
          isActive ? 'opacity-100' : 'opacity-40',
        )}
        weight="bold"
        aria-hidden
      />
    </button>
  );
}
